import { DragHandleIcon } from '@chakra-ui/icons';
import { Box, Center, Flex, HStack, Text, useBreakpointValue } from '@chakra-ui/react';
import { countriesInfo, type Country } from '@/models/country';
import { Flag } from './Flag';

export function CountryTag(props: {
  country: Country;
  opaque?: boolean;
  hasShadow?: boolean;
  draggable?: boolean;
  /** Tighter layout for edit-list ranking column */
  compact?: boolean;
  /** Slightly larger type/flag when ranking in the edit-list modal (desktop) */
  rankList?: boolean;
  /**
   * Edit-list / Rules preview: flag height as a fraction of the row box (0–1), left-aligned.
   * Uniform within the rank column because every row shares the same height.
   */
  flagBoxFill?: number;
  /** Stretch tag to the full rank-row height (edit list). */
  fillHeight?: boolean;
  /** Rank row height (px) — used with flagBoxFill for a visible left flag. */
  rowHeightPx?: number;
  /** Truncate name to one line (Rules rank preview). */
  nameSingleLine?: boolean;
  /** Override label size (px) — e.g. Rules rank column. */
  labelFontSize?: number;
  /** Larger drag affordance for touch (mobile edit-list ranking). */
  touchDragHandle?: boolean;
}) {
  const defaultNameSize = useBreakpointValue({ base: 16, sm: 17 });
  const compactNameSize = useBreakpointValue(
    props.rankList ? { base: 13, sm: 14 } : { base: 12, sm: 13 },
  );
  const editListNameSize = useBreakpointValue({ base: 11, sm: 12 });
  const useBoxFill = props.compact && props.flagBoxFill != null;
  const nameFontSize =
    props.labelFontSize ??
    (useBoxFill
      ? (editListNameSize ?? 11)
      : ((props.compact ? compactNameSize : defaultNameSize) ?? (props.compact ? 12 : 16)));

  const stackSpacing = useBoxFill ? 2 : props.compact ? (props.rankList ? 2.5 : 2) : 3;
  const stackPx = useBoxFill ? 1.5 : props.compact ? (props.rankList ? 2 : 1.5) : 1.5;
  const stackPy = useBoxFill ? 1 : props.compact ? (props.rankList ? 2 : 1.5) : 1.5;

  const editListVerticalPadPx = stackPy * 4;
  const flagMinPx = props.flagBoxFill != null && props.flagBoxFill < 0.5 ? 8 : 12;
  const flagHeightPx =
    useBoxFill && props.rowHeightPx != null
      ? Math.max(
          flagMinPx,
          Math.round((props.rowHeightPx - editListVerticalPadPx) * props.flagBoxFill!),
        )
      : undefined;

  if (props.compact) {
    return (
      <HStack
        background={props.opaque ? 'listItemBackgroundOpaque' : 'listItemBackground'}
        borderRadius={6}
        spacing={stackSpacing}
        pl={stackPx}
        pr={props.draggable ? (props.rankList ? 2 : 2) : stackPx}
        py={stackPy}
        width='full'
        h={props.fillHeight ? '100%' : undefined}
        minH={props.fillHeight ? 0 : undefined}
        align='stretch'
        userSelect={'none'}
        boxShadow={props.hasShadow ? '0px 0px 3px 0.3px #3336' : undefined}
      >
        {useBoxFill ? (
          <Flex
            flexShrink={0}
            h='100%'
            alignSelf='stretch'
            alignItems='center'
            justifyContent='flex-start'
            pr={1}
          >
            <Flag
              country={props.country}
              borderRadius={3}
              {...(flagHeightPx != null ? { h: `${flagHeightPx}px` } : { w: 6 })}
              flexShrink={0}
            />
          </Flex>
        ) : (
          <Center flexShrink={0} w={props.rankList ? 11 : 10} alignSelf='stretch'>
            <Flag country={props.country} borderRadius={3} w={props.rankList ? 7 : 6} />
          </Center>
        )}
        <Flex flex={1} align='center' justify={useBoxFill ? 'flex-start' : 'center'} minW={0}>
          <Text
            width='full'
            textAlign={useBoxFill ? 'left' : 'center'}
            fontSize={nameFontSize}
            fontWeight='semibold'
            lineHeight={1.45}
            noOfLines={props.nameSingleLine ? 1 : 3}
            whiteSpace={props.nameSingleLine ? 'nowrap' : undefined}
            overflow={props.nameSingleLine ? 'hidden' : undefined}
            textOverflow={props.nameSingleLine ? 'ellipsis' : undefined}
          >
            {countriesInfo[props.country].name}
          </Text>
        </Flex>
        {props.draggable ? (
          <Center flexShrink={0} alignSelf='center' px={props.touchDragHandle ? 0.5 : 0}>
            <DragHandleIcon
              fontSize={props.touchDragHandle ? 14 : props.rankList ? 12 : 10}
            />
          </Center>
        ) : null}
      </HStack>
    );
  }

  const flagW = 8;
  const flagGutterW = 10;

  return (
    <HStack
      background={props.opaque ? 'listItemBackgroundOpaque' : 'listItemBackground'}
      borderRadius={6}
      spacing={stackSpacing}
      pl={stackPx}
      pr={props.draggable ? 2.5 : stackPx}
      py={stackPy}
      width='full'
      userSelect={'none'}
      boxShadow={props.hasShadow ? '0px 0px 3px 0.3px #3336' : undefined}
    >
      <Box minW={flagGutterW}>
        <Flag country={props.country} borderRadius={3} w={flagW} />
      </Box>
      <Box width='full' minW={0}>
        <Text
          width='full'
          textAlign={'center'}
          fontSize={nameFontSize}
          fontWeight='semibold'
          lineHeight={1.35}
          noOfLines={2}
        >
          {countriesInfo[props.country].name}
        </Text>
      </Box>
      {props.draggable ? <DragHandleIcon fontSize={12} /> : null}
    </HStack>
  );
}
